/**
 * bigdough.js — BigDough upload pipeline, backend-native (no Sheets).
 * File parsing is client-side. All data ops go through the backend API.
 */

import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const BASE = import.meta.env.VITE_BACKEND_BASE_URL ?? 'https://sales-dashboard-backend-842367857924.us-west2.run.app';

async function _req(getToken, path, options = {}) {
  const token = await getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { msg = (await res.json())?.error ?? msg; } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

// ── Client-side file parsing ──────────────────────────────────────────────────

export function detectBigDoughFormat(headers) {
  const h = headers.map((s) => String(s).trim().toLowerCase());
  const has = (aliases) => aliases.some((a) => h.includes(a.toLowerCase()));
  return {
    isCampaignReport: has(['email opened', 'read count', 'status']),
    hasSubject:       has(['subject', 'email subject']),
    hasDate:          has(['send date', 'date sent', 'sent', 'send_date', 'date']),
    hasEmail:         has(BD_COLUMN_MAP.email.map((a) => a.toLowerCase())),
  };
}

// The bulk-download tool names files "{emailId}_{M}_{D}_{YYYY}-{h}_{mm}_{ss}-{AM|PM}_{subject}.xlsx"
// (e.g. "21745116_5_8_2026-11_03_47-AM_wells financials conf - acgl, amp....xlsx").
// Pulls subject + send date straight out of the filename so a batch of these
// doesn't need per-file manual entry.
const BD_BULK_FILENAME_RE = /^(\d+)_(\d{1,2})_(\d{1,2})_(\d{4})-(\d{1,2})_(\d{2})_(\d{2})-(AM|PM)_(.+)\.(xlsx|xls)$/i;

export function parseBdBulkFilename(fileName) {
  const m = BD_BULK_FILENAME_RE.exec(fileName.trim());
  if (!m) return null;
  const [, emailId, month, day, year, hour12, , , ampm, subject] = m;
  const campaignDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  // 24h hour, straight from the filename's own timestamp — this is the only
  // place send-time-of-day is ever captured (BD has no API, and the
  // per-recipient export itself has no "date sent" column of its own).
  const h12 = parseInt(hour12, 10);
  const isPM = /pm/i.test(ampm);
  const sendHour = h12 === 12 ? (isPM ? 12 : 0) : (isPM ? h12 + 12 : h12);
  return { emailId, subject: subject.trim(), campaignDate, sendHour };
}

export async function parseBigDoughFile(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'csv') return _parseCSV(file);
  if (ext === 'xlsx' || ext === 'xls') return _parseXLS(file);
  throw new Error(`Unsupported file type: .${ext}. Please upload a CSV or XLS/XLSX file.`);
}

function _parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true, skipEmptyLines: true, trimHeaders: true,
      complete: (r) => resolve({ headers: r.meta.fields ?? [], rows: r.data }),
      error: reject,
    });
  });
}

async function _parseXLS(file) {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  return { headers: rows.length > 0 ? Object.keys(rows[0]) : [], rows };
}

// ── Row normalisation ─────────────────────────────────────────────────────────

const BD_COLUMN_MAP = {
  email:           ['E-mail', 'Email', 'email', 'Email Address', 'Recipient Email', 'Recipient'],
  status:          ['Status', 'status', 'Delivery Status'],
  name:            ['Name', 'name', 'Full Name', 'Contact Name', 'Recipient Name'],
  institution:     ['Institution', 'institution', 'Company', 'Firm', 'Account Name'],
  phone:           ['Phone', 'phone', 'Phone Number'],
  send_date:       ['Send Date', 'Date Sent', 'Sent', 'send_date', 'Date'],
  subject:         ['Subject', 'Email Subject', 'subject'],
  opened:          ['Opened', 'Email Opened', 'Open', 'opened', 'Was Opened'],
  clicked:         ['URL Link Views', 'File Link Views', 'Clicked', 'Click', 'clicked', 'Was Clicked'],
  read_count:      ['Read Count', 'read_count', 'Reads'],
  url_link_views:  ['URL Link Views', 'url_link_views'],
  file_link_views: ['File Link Views', 'file_link_views'],
  bd_send_id:      ['BD Send ID', 'Send ID', 'bd_send_id'],
  bd_contact_id:   ['BD Contact ID', 'Contact ID', 'bd_contact_id', 'contact_id'],
  bd_mandate:      ['Mandate', 'bd_mandate', 'Strategy', 'Fund Strategy'],
  bd_aum_personal: ['AUM Personal', 'bd_aum_personal', 'Personal AUM', 'AUM'],
};

function _findValue(row, aliases) {
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(row, alias)) return row[alias] ?? '';
  }
  return '';
}

function _normBool(v) {
  if (!v || v === false) return false;
  if (typeof v === 'number') return v > 0;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'no' && s !== '0';
}

function _normaliseRow(rawRow, i) {
  const out = { row_index: i };
  for (const [col, aliases] of Object.entries(BD_COLUMN_MAP)) {
    const val = _findValue(rawRow, aliases);
    if (col === 'opened' || col === 'clicked') out[col] = _normBool(val) ? 'True' : 'False';
    else if (val !== '') out[col] = String(val);
  }
  // The "Email Opened" column is itself a timestamp in BD's per-recipient
  // export (e.g. "6/1/2026 3:48:53 PM", blank if never opened) — `opened`
  // above only keeps the True/False boolean derived from it, so capture the
  // raw value separately here for bd_raw_rows.email_opened_at.
  const openedRaw = _findValue(rawRow, BD_COLUMN_MAP.opened);
  if (openedRaw !== '' && !isNaN(new Date(openedRaw))) {
    out.email_opened_at = new Date(openedRaw).toISOString();
  }
  return out;
}

function _deriveCampaignDate(rows) {
  let earliest = null;
  for (const row of rows) {
    const v = _findValue(row, ['Email Opened', 'Opened', 'Open']);
    if (!v) continue;
    const d = new Date(v);
    if (!isNaN(d) && (!earliest || d < earliest)) earliest = d;
  }
  if (!earliest) return new Date().toISOString().slice(0, 10);
  const y = earliest.getFullYear();
  const m = String(earliest.getMonth() + 1).padStart(2, '0');
  const d = String(earliest.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ── Upload ────────────────────────────────────────────────────────────────────

/**
 * importBigDough — normalises rows and POSTs to /api/bd-import.
 * Backend processes everything synchronously; returns the result directly.
 * onProgress(0→100) is called during the request.
 */
export async function importBigDough(getToken, rows, onProgress = () => {}, overrides = {}) {
  if (!rows || rows.length === 0) throw new Error('No rows to upload.');

  onProgress(10);

  const hasPerRowDate = rows.some((r) => {
    const v = _findValue(r, BD_COLUMN_MAP.send_date);
    return v !== null && v !== undefined && String(v).trim() !== '';
  });
  const campaignDate = overrides.campaign_date
    || overrides.send_date
    || (hasPerRowDate ? '' : _deriveCampaignDate(rows));

  const normalised = rows.map((raw, i) => _normaliseRow(raw, i));
  onProgress(20);

  const result = await _req(getToken, '/api/bd-import', {
    method: 'POST',
    body: JSON.stringify({
      rows:          normalised,
      subject:       overrides.subject    ?? '',
      campaign_date: campaignDate,
      send_hour:     overrides.send_hour ?? '',
      file_name:     overrides.file_name  ?? '',
      body:          overrides.body       ?? '',
      body_html:     overrides.body_html  ?? '',
      body_format:   overrides.body_format ?? '',
      body_source:   overrides.body_source ?? 'manual',
      list_name:     overrides.list_name   ?? '',
      reconcile_upload_id: overrides.reconcile_upload_id ?? '',
      force_reimport: overrides.force_reimport === true,
    }),
  });

  onProgress(100);
  return result; // { upload_id, total_delivered, new_contacts, queued_contacts, added_to_log, total_rows, filtered_out }
}

// ── Upload history ────────────────────────────────────────────────────────────

// Match rows from an external campaign-tracking export (date/subject/
// recipients/list name — no per-recipient detail) against existing
// bd_uploads by date + subject similarity, and stamp list_name onto the
// match. rows: [{ sent_at, recipients, subject, list_names }].
// Returns { matched: [...], unmatched: [...] }.
export function backfillListNames(getToken, rows) {
  return _req(getToken, '/api/bd-uploads/backfill-list-names', {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}

export function fetchUploadHistory(getToken, { limit = 500 } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set('limit', String(limit));
  const query = params.toString();
  return _req(getToken, `/api/bd-uploads${query ? `?${query}` : ''}`);
}

export function updateUpload(getToken, uploadId, fields) {
  return _req(getToken, `/api/bd-uploads/${encodeURIComponent(uploadId)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}

export function deleteUpload(getToken, uploadId) {
  return _req(getToken, `/api/bd-uploads/${encodeURIComponent(uploadId)}`, { method: 'DELETE' });
}

// ── Contact candidates ────────────────────────────────────────────────────────

export function fetchContactCandidates(getToken, { status = 'pending', limit = 200 } = {}) {
  const p = new URLSearchParams({ status, limit: String(limit) });
  return _req(getToken, `/api/bd-contact-candidates?${p}`);
}

export function approveContactCandidate(getToken, candidateId, body) {
  return _req(getToken, `/api/bd-contact-candidates/${encodeURIComponent(candidateId)}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function dismissContactCandidate(getToken, candidateId) {
  return _req(getToken, `/api/bd-contact-candidates/${encodeURIComponent(candidateId)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
